(() => {
  const root = window.Shopify?.routes?.root || "/";

  const postJSON = async (url, body) => {
    const res = await fetch(root + url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.description || data?.message || data?.errors || "Request failed");
    return data;
  };

  const getCart = async () => (await fetch(root + "cart.js")).json();

  const decodeHtmlEntities = (text) => {
    if (!text) return "";
    const textarea = document.createElement("textarea");
    textarea.innerHTML = text;
    return textarea.value;
  };

  const setMsg = (wrap, msg, isErr = false) => {
    const el = wrap.querySelector("[data-mh-discount-msg]");
    if (!el) return;
    el.textContent = decodeHtmlEntities(msg || "");
    el.classList.toggle("text-error", isErr);
  };

  const formatMsg = (template, code) => {
    if (!template) return "";
    return template.replace(/\{\{\s*code\s*\}\}/gi, code);
  };

  const promoMsg = (wrap, key, code = "") => {
    const template = decodeHtmlEntities(wrap.dataset[key] || "");
    return code ? formatMsg(template, code) : template;
  };

  const findGiftLine = (cart, giftVariantId) =>
    (cart.items || []).find(
      (item) =>
        Number(item.variant_id) === Number(giftVariantId) &&
        item.properties?._mh_free_gift === "true"
    );

  const fetchVariant = async (variantId) => {
    const res = await fetch(`${root}variants/${variantId}.js`);
    if (!res.ok) return null;
    return res.json().catch(() => null);
  };

  const isGiftInStock = (variant) => {
    if (!variant || variant.available !== true) return false;

    if (variant.inventory_management === "shopify" && Number(variant.inventory_quantity) < 1) {
      return false;
    }

    return true;
  };

  const ensureGiftAvailable = async (giftVariantId) => {
    const variant = await fetchVariant(giftVariantId);
    if (!isGiftInStock(variant)) {
      throw new Error("__GIFT_OUT_OF_STOCK__");
    }
  };

  const giftOutOfStockMsg = (wrap) =>
    promoMsg(wrap, "giftOutOfStock") || "The free gift is currently out of stock.";

  const isStockError = (msg) => {
    const m = String(msg || "").toLowerCase();
    return (
      m === "__gift_out_of_stock__" ||
      /sold out|out of stock|not enough|inventory|unavailable|缺货|没有库存/.test(m)
    );
  };

  const addGiftIfMissing = async (giftVariantId) => {
    const cart = await getCart();
    if (findGiftLine(cart, giftVariantId)) return;

    await postJSON("cart/add.js", {
      items: [{ id: Number(giftVariantId), quantity: 1, properties: { _mh_free_gift: "true" } }],
    });
  };

  const removeGift = async (giftVariantId) => {
    const cart = await getCart();
    const gift = findGiftLine(cart, giftVariantId);
    if (!gift) return false;
    await postJSON("cart/change.js", { id: gift.key, quantity: 0 });
    return true;
  };

  const applyDiscount = async (code) => {
    const trimmed = (code || "").trim();
    const res = await fetch(root + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ discount: trimmed }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.description || data?.message || data?.errors || "Request failed");
    return data;
  };

  const codeRow = (cart, code) => {
    const target = (code || "").trim().toLowerCase();
    return (cart.discount_codes || []).find((d) => (d.code || "").toLowerCase() === target);
  };

  const giftIsFree = (cart, giftVariantId) => {
    const gift = findGiftLine(cart, giftVariantId);
    return !!gift && Number(gift.final_line_price) === 0;
  };

  const isGiftFlow = (wrap, code) => {
    if (wrap.dataset.giftEnabled !== "true") return false;
    const giftCode = (wrap.dataset.giftCode || "").trim().toLowerCase();
    return giftCode !== "" && code.toLowerCase() === giftCode;
  };

  const cartHasExistingDiscounts = (cart, code) => {
    const codeLower = code.toLowerCase();
    const applications = cart.cart_level_discount_applications || [];

    if (applications.some((app) => app.type === "automatic" || app.type === "script")) {
      return true;
    }

    if (applications.some((app) => (app.title || "").toLowerCase() !== codeLower)) {
      return true;
    }

    return (cart.items || []).some((item) => (item.line_level_discount_allocations || []).length > 0);
  };

  const resolveDiscountError = (cart, code, wrap, apiMessage = "") => {
    const api = String(apiMessage || "").toLowerCase();

    if (/already been used|already used/.test(api)) {
      return promoMsg(wrap, "discountAlreadyUsed", code) || formatMsg("The {{ code }} discount code has already been used", code);
    }

    if (/better combination/.test(api)) {
      return (
        promoMsg(wrap, "discountCantCombineBetter", code) ||
        formatMsg("{{ code }} couldn't be used with your existing discounts. You already have a better combination", code)
      );
    }

    if (/couldn't be used with your existing|could not be used with your existing|existing discounts/.test(api)) {
      return promoMsg(wrap, "discountCantCombineExisting") || "Discount couldn't be used with your existing discounts";
    }

    if (/couldn't be used together|could not be used together|best combination/.test(api)) {
      return (
        promoMsg(wrap, "discountCantCombine") ||
        "Some discount codes couldn't be used together. We applied the best combination."
      );
    }

    const row = codeRow(cart, code);
    const codes = cart.discount_codes || [];

    if (!row) {
      return promoMsg(wrap, "discountError") || "Enter a valid discount code";
    }

    const applicableCount = codes.filter((d) => d.applicable).length;
    const inapplicableCount = codes.filter((d) => !d.applicable).length;

    if (codes.length > 1 && applicableCount > 0 && inapplicableCount > 0) {
      return (
        promoMsg(wrap, "discountCantCombine") ||
        "Some discount codes couldn't be used together. We applied the best combination."
      );
    }

    if (cartHasExistingDiscounts(cart, code)) {
      return (
        promoMsg(wrap, "discountCantCombineBetter", code) ||
        formatMsg("{{ code }} couldn't be used with your existing discounts. You already have a better combination", code)
      );
    }

    return (
      promoMsg(wrap, "discountNotValidForCart", code) ||
      formatMsg("{{ code }} discount code is not valid for the items in your cart", code)
    );
  };

  const resolveGiftFlowError = (cart, code, wrap) => {
    const api = String(resolveDiscountError(cart, code, wrap) || "").toLowerCase();

    if (/already been used|already used|已被使用/.test(api)) {
      return resolveDiscountError(cart, code, wrap);
    }

    if (/couldn't be used|could not be used|best combination|无法.*同时使用|已有更优/.test(api)) {
      return resolveDiscountError(cart, code, wrap);
    }

    return giftOutOfStockMsg(wrap);
  };

  const refreshCartUI = () => {
    if (window.themeVariables?.settings?.pageType === "cart") {
      location.reload();
      return;
    }
    document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
  };

  const rollback = async (giftVariantId) => {
    await applyDiscount("");
    if (giftVariantId) await removeGift(giftVariantId);
  };

  const reconcileGift = async (wrap) => {
    const giftVariantId = Number(wrap.dataset.giftVariantId || 0);
    if (!giftVariantId) return;

    const cart = await getCart();
    if (!findGiftLine(cart, giftVariantId)) return;

    const giftEnabled = wrap.dataset.giftEnabled === "true";
    const giftCode = (wrap.dataset.giftCode || "").trim().toLowerCase();
    const activeCodes = (cart.discount_codes || []).filter((d) => d.applicable);
    const hasGiftCode =
      giftEnabled && giftCode && activeCodes.some((d) => (d.code || "").toLowerCase() === giftCode);

    if (!hasGiftCode) {
      const removed = await removeGift(giftVariantId);
      if (removed) refreshCartUI();
    }
  };

  const bindPromo = (wrap) => {
    const giftVariantId = Number(wrap.dataset.giftVariantId || 0);
    const input = wrap.querySelector("[data-mh-discount-input]");
    const btnApply = wrap.querySelector("[data-mh-discount-apply]");

    if (!input || !btnApply) return;
    if (wrap.dataset.mhPromoBound) return;
    wrap.dataset.mhPromoBound = "true";

    const onApply = async () => {
      const code = (input.value || "").trim();
      if (!code) return setMsg(wrap, "Please enter a discount code.", true);

      btnApply.disabled = true;

      try {
        setMsg(wrap, "Checking…");

        const cartBefore = await getCart();
        const giftFlow = isGiftFlow(wrap, code);

        if (giftFlow) {
          if (!giftVariantId) {
            setMsg(wrap, promoMsg(wrap, "discountError"), true);
            return;
          }

          const minimumSpend = Number(wrap.dataset.minimumSpend || 0);
          if (minimumSpend > 0 && cartBefore.items_subtotal_price < minimumSpend) {
            const remaining = ((minimumSpend - cartBefore.items_subtotal_price) / 100).toFixed(2);
            setMsg(wrap, `Spend $${remaining} more to qualify for the free gift.`, true);
            return;
          }

          setMsg(wrap, "Checking gift availability…");
          await ensureGiftAvailable(giftVariantId);

          setMsg(wrap, "Adding gift…");
          await addGiftIfMissing(giftVariantId);

          const cartWithGift = await getCart();
          if (!findGiftLine(cartWithGift, giftVariantId)) {
            throw new Error("__GIFT_OUT_OF_STOCK__");
          }
        } else if (giftVariantId) {
          await removeGift(giftVariantId);
        }

        setMsg(wrap, "Applying code…");
        const cartAfter = await applyDiscount(code);
        const row = codeRow(cartAfter, code);

        if (!row || row.applicable !== true) {
          await rollback(giftFlow ? giftVariantId : 0);
          setMsg(
            wrap,
            giftFlow ? resolveGiftFlowError(cartAfter, code, wrap) : resolveDiscountError(cartAfter, code, wrap),
            true
          );
          return;
        }

        if (giftFlow) {
          if (!giftIsFree(cartAfter, giftVariantId)) {
            await rollback(giftVariantId);
            setMsg(wrap, resolveGiftFlowError(cartAfter, code, wrap), true);
            return;
          }
        }

        setMsg(wrap, "");
        refreshCartUI();
      } catch (e) {
        if (isStockError(e.message)) {
          setMsg(wrap, giftOutOfStockMsg(wrap), true);
        } else {
          setMsg(wrap, resolveDiscountError({}, code, wrap, e.message), true);
        }
      } finally {
        btnApply.disabled = false;
      }
    };

    btnApply.addEventListener("click", onApply);
    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        onApply();
      }
    });
  };

  const init = () => {
    document.querySelectorAll("[data-mh-promo]").forEach((wrap) => {
      bindPromo(wrap);
      reconcileGift(wrap);
    });
  };

  document.addEventListener("DOMContentLoaded", init);
  document.addEventListener("cart-drawer:refreshed", init);
  document.addEventListener("cart:refresh", () => {
    document.querySelectorAll("[data-mh-promo]").forEach(reconcileGift);
  });
})();
